import { GameRow } from "./data";
import { avatarDataUriForName } from "./avatar";

// This type represents the shape of the game data returned by our new listActiveGamesByUser method
type GameWithPlayerNames = GameRow & {
    p1: { user_name: string; profile_image_url?: string } | null;
    p2: { user_name: string; profile_image_url?: string } | null;
};

export class ActiveGamesDisplay {
    private onGameSelected: (game: GameWithPlayerNames) => void;
    private onForfeitRequested: (gameId: string, isMyTurn: boolean, updatedAt: string) => void;

    constructor(
        private container: HTMLElement,
        onGameSelected: (game: GameWithPlayerNames) => void,
        onForfeitRequested: (gameId: string, isMyTurn: boolean, updatedAt: string) => void
    ) {
        this.onGameSelected = onGameSelected;
        this.onForfeitRequested = onForfeitRequested;
    }

    public show(games: GameWithPlayerNames[], currentUserId: string, lastSeenTime: number) {
        this.container.innerHTML = ''; // Clear previous content
        this.container.style.display = 'flex';

        if (games.length === 0) {
            this.showEmptyState();
            return;
        }

        const list = document.createElement('div');
        list.className = 'active-games-list';

        games.forEach(game => {
            const isPlayer1 = game.p1_user_id === currentUserId;
            const myPlayerIndex = isPlayer1 ? 0 : 1;
            const isMyTurn = game.game_state_json?.currentPlayer === myPlayerIndex;
            const gamePhase = game.game_state_json?.phase;
            const opponent = isPlayer1 ? game.p2 : game.p1;
            const opponentName = opponent?.user_name || 'Unknown';
            const opponentAvatar = opponent?.profile_image_url || avatarDataUriForName(opponentName);
            const isArchived = game.status === 'completed' || game.status === 'forfeited';

            // Determine final/visible score, including sudden death finals for archived games
            const sd = Array.isArray(game.game_state_json?.suddenDeathScores) ? game.game_state_json.suddenDeathScores : null as any;
            const sdValid = !!(sd && (Number(sd[0]) > 0 || Number(sd[1]) > 0));
            const baseScores = Array.isArray(game.game_state_json?.scores) ? game.game_state_json.scores : null as any;
            let finalA: number | null = null;
            let finalB: number | null = null;
            let usedSuddenDeath = false;
            if (sdValid) {
                finalA = Number(sd[0]);
                finalB = Number(sd[1]);
                usedSuddenDeath = true;
            } else if (baseScores && baseScores.length >= 2) {
                const a = Number(baseScores[0]);
                const b = Number(baseScores[1]);
                if (Number.isFinite(a) && Number.isFinite(b)) { finalA = a; finalB = b; }
            } else if (Number.isFinite((game as any).p1_score) && Number.isFinite((game as any).p2_score)) {
                finalA = Number((game as any).p1_score);
                finalB = Number((game as any).p2_score);
            }
            // Default score display; later we show current user's score first in active view
            let score = '0 - 0';
            if (finalA != null && finalB != null) {
                const myFirst = isPlayer1 ? finalA : finalB;
                const oppSecond = isPlayer1 ? finalB : finalA;
                score = `${myFirst} - ${oppSecond}`;
            }

            const gameCard = document.createElement('div');
            gameCard.className = 'skeech-card game-card';
            if (isMyTurn && game.status !== 'forfeited' && !isArchived) {
                gameCard.classList.add('my-turn');
            }

            if (isArchived) {
                if (game.status === 'forfeited') {
                    gameCard.classList.add('forfeited');
                    const forfeitStamp = document.createElement('div');
                    forfeitStamp.className = 'forfeit-stamp';
                    forfeitStamp.textContent = 'OPPONENT FORFEIT';
                    gameCard.appendChild(forfeitStamp);
                }
                // Archived games: click to open; no forfeit gestures
                gameCard.onclick = () => this.onGameSelected(game);
            } else {
                // --- Gesture Handling ---
                let pressTimer: number;

                const showForfeitConfirmation = () => {
                    this.onForfeitRequested(game.id, isMyTurn, game.updated_at);
                    
                    // Clean up listeners on this card to prevent memory leaks
                    gameCard.removeEventListener('pointerup', onPointerUp);
                    gameCard.removeEventListener('pointerleave', onPointerUp);
                    gameCard.removeEventListener('pointerdown', onPointerDown);
                    gameCard.removeEventListener('contextmenu', onContextMenu);
                };

                const onPointerUp = () => {
                    clearTimeout(pressTimer);
                };

                const onPointerDown = (e: PointerEvent) => {
                    // Only trigger for left-click/touch
                    if (e.button !== 0) return;
                    pressTimer = window.setTimeout(() => {
                        showForfeitConfirmation();
                    }, 500); // 500ms for long press
                };

                const onContextMenu = (e: MouseEvent) => {
                    e.preventDefault();
                    showForfeitConfirmation();
                };

                // Regular click to enter the game
                gameCard.onclick = () => this.onGameSelected(game);

                // Add gesture event listeners
                gameCard.addEventListener('pointerdown', onPointerDown);
                gameCard.addEventListener('pointerup', onPointerUp);
                gameCard.addEventListener('pointerleave', onPointerUp); // Cancel on leave
                gameCard.addEventListener('contextmenu', onContextMenu);
            }

            // --- Element Creation ---
            const opponentDiv = document.createElement('div');
            opponentDiv.style.fontWeight = '600';
            opponentDiv.style.display = 'flex';
            opponentDiv.style.alignItems = 'center';
            opponentDiv.style.gap = '10px';

            const avatar = document.createElement('img');
            avatar.src = opponentAvatar;
            avatar.style.width = '32px';
            avatar.style.height = '32px';
            avatar.style.borderRadius = '50%';
            avatar.style.objectFit = 'cover';
            avatar.style.flexShrink = '0';

            const nameSpan = document.createElement('span');
            nameSpan.textContent = opponentName;

            opponentDiv.appendChild(avatar);
            opponentDiv.appendChild(nameSpan);

            const scoreLine = document.createElement('div');
            scoreLine.style.fontSize = '12px';
            scoreLine.style.opacity = '0.8';
            scoreLine.innerHTML = `Score: ${score}`;

            if (!isArchived && gamePhase === 'retribution') {
                const retributionIndicator = document.createElement('span');
                retributionIndicator.className = 'retribution-indicator dancing';
                retributionIndicator.textContent = '🔪';
                scoreLine.appendChild(document.createTextNode(' '));
                scoreLine.appendChild(retributionIndicator);
            } else if (!isArchived && gamePhase === 'sudden_death') {
                const suddenDeathIndicator = document.createElement('span');
                suddenDeathIndicator.className = 'sudden-death-indicator dancing';
                suddenDeathIndicator.textContent = '💀';
                scoreLine.appendChild(document.createTextNode(' '));
                scoreLine.appendChild(suddenDeathIndicator);
            }

            const roundDiv = document.createElement('div');
            roundDiv.style.fontSize = '12px';
            roundDiv.style.opacity = '0.8';
            if (!isArchived) {
                roundDiv.textContent = `Round: ${game.game_state_json?.round || 1}`;
            } else {
                roundDiv.style.display = 'none';
            }

            const turnDiv = document.createElement('div');
            turnDiv.style.marginTop = '8px';
            turnDiv.style.fontWeight = 'bold';
            turnDiv.style.color = isMyTurn ? '#4CAF50' : '#ccc';
            turnDiv.textContent = isMyTurn ? 'Your Turn' : "Opponent's Turn";
            if (isArchived || game.status === 'forfeited') {
                turnDiv.style.display = 'none';
            }

            // Use right side space for status/round
            const row = document.createElement('div');
            row.style.display = 'flex';
            row.style.alignItems = 'center';
            row.style.justifyContent = 'space-between';
            row.style.gap = '16px';

            const leftCol = document.createElement('div');
            leftCol.style.display = 'flex';
            leftCol.style.flexDirection = 'column';
            leftCol.style.gap = '4px';
            if (isArchived) {
                leftCol.style.flexDirection = 'row';
                leftCol.style.alignItems = 'center';
                leftCol.style.gap = '12px';
                // Date column (start date): MON\nDD
                const ts = (game as any).time_stamp || game.updated_at;
                const started = ts ? new Date(ts) : null;
                const month = started ? started.toLocaleString('en-US', { month: 'short' }).toUpperCase() : '';
                const day = started ? String(started.getDate()) : '';
                const dateCol = document.createElement('div');
                dateCol.className = 'game-date-badge';
                dateCol.style.display = 'flex';
                dateCol.style.flexDirection = 'column';
                dateCol.style.alignItems = 'center';
                dateCol.style.justifyContent = 'center';
                dateCol.style.width = '44px';
                const monDiv = document.createElement('div');
                monDiv.textContent = month;
                monDiv.className = 'game-date-badge-month';
                const dayDiv = document.createElement('div');
                dayDiv.textContent = day;
                dayDiv.className = 'game-date-badge-day';
                dateCol.appendChild(monDiv);
                dateCol.appendChild(dayDiv);
                leftCol.appendChild(dateCol);
                leftCol.appendChild(opponentDiv);
            } else {
                leftCol.appendChild(opponentDiv);
                leftCol.appendChild(scoreLine);
            }

            const rightCol = document.createElement('div');
            rightCol.style.display = 'flex';
            rightCol.style.flexDirection = 'column';
            rightCol.style.alignItems = 'flex-end';
            rightCol.style.gap = '4px';
            rightCol.style.minWidth = '80px';

            if (isArchived) {
                if (game.status === 'completed' && finalA != null && finalB != null) {
                    // Build horizontal row: W/L then score (with optional SD)
                    const rightRow = document.createElement('div');
                    rightRow.style.display = 'flex';
                    rightRow.style.alignItems = 'center';
                    rightRow.style.gap = '10px';
                    const myFinal = isPlayer1 ? finalA : finalB;
                    const oppFinal = isPlayer1 ? finalB : finalA;
                    if (myFinal !== oppFinal) {
                        const wl = document.createElement('div');
                        const isWin = myFinal > oppFinal;
                        wl.textContent = isWin ? 'WIN' : 'LOSS';
                        wl.style.fontWeight = '800';
                        wl.style.fontSize = '14px';
                        wl.style.letterSpacing = '0.8px';
                        wl.style.color = isWin ? '#4CAF50' : '#E53935';
                        rightRow.appendChild(wl);
                    }
                    const scoreText = document.createElement('div');
                    scoreText.textContent = `${myFinal} - ${oppFinal}`;
                    scoreText.style.fontWeight = '600';
                    rightRow.appendChild(scoreText);
                    if (usedSuddenDeath) {
                        const sd = document.createElement('div');
                        sd.textContent = '(SD)';
                        sd.style.fontSize = '12px';
                        sd.style.opacity = '0.8';
                        rightRow.appendChild(sd);
                    }
                    rightCol.appendChild(rightRow);
                    // Also show total rounds
                    const roundsRight = document.createElement('div');
                    roundsRight.style.fontSize = '12px';
                    roundsRight.style.opacity = '0.8';
                    const roundsVal = (game as any).rounds || game.game_state_json?.round || 1;
                    roundsRight.textContent = `R: ${roundsVal}`;
                    rightCol.appendChild(roundsRight);
                }
                // For forfeited archived games, keep existing stamp and omit W/L/score here
            } else {
                const roundRight = document.createElement('div');
                roundRight.style.fontSize = '12px';
                roundRight.style.opacity = '0.8';
                roundRight.textContent = `Round: ${game.game_state_json?.round || 1}`;
                rightCol.appendChild(roundRight);
            }

            row.appendChild(leftCol);
            row.appendChild(rightCol);

            gameCard.appendChild(row);
            if (!isArchived) gameCard.appendChild(turnDiv);
            list.appendChild(gameCard);
        });

        this.container.appendChild(list);
    }

    public hide() {
        this.container.style.display = 'none';
    }

    private showEmptyState() {
        const emptyState = document.createElement('div');
        emptyState.className = 'active-games-empty-state';

        emptyState.innerHTML = `
            <div class="empty-state-message">Start Skrolling!</div>            
            <div class="empty-state-subtext">Challenge a player below</div>
            <div class="empty-state-arrow">&#8681;</div>
        `;

        this.container.appendChild(emptyState);
    }
}
